// ============================================================
// schedulerPatch — 排班 Optimistic Update 輔助（獨立檔）
// ------------------------------------------------------------
// 修復 Bug：點擊格子後 200-500ms 無視覺反饋（等 POST 返回）。
// 策略：先塞「暫定項目」到 state，POST 成功後替換、失敗則移除。
//
// 使用方式（取代 scheduleStore.ts 第 188-232 行的 createSchedule / toggleCell）：
//   import { optimisticCreate } from './schedulerPatch';
//   createSchedule: async (data) => optimisticCreate(set, get, data),
// ============================================================

import { api } from '../lib/api';

interface ScheduleEntryLite {
  id: string;
  userId: string;
  date: string;
  startTime: string;
  endTime: string;
  breakStart?: string | null;
  breakEnd?: string | null;
  // kind 要一起帶：休息日／例假日的卡片長相不同，樂觀更新當下就得畫對
  template?: { id: string; name: string; color: string; kind?: 'WORK' | 'REST_DAY' | 'REGULAR_LEAVE' } | null;
  isPublished?: boolean;
  isOptimistic?: boolean; // 新增：標記暫定項目
}

interface CreateData {
  userId: string;
  date: string;
  startTime: string;
  endTime: string;
  breakStart?: string;
  breakEnd?: string;
  templateId?: string;
  position?: string;
  note?: string;
}

/**
 * 樂觀新增：先顯示暫定項目，POST 成功後換成真實資料
 */
export async function optimisticCreate(
  set: (fn: (state: { schedules: ScheduleEntryLite[] }) => Partial<{ schedules: ScheduleEntryLite[] }>) => void,
  get: () => { schedules: ScheduleEntryLite[]; templates?: { id: string; name: string; color: string; kind?: 'WORK' | 'REST_DAY' | 'REGULAR_LEAVE' }[] },
  data: CreateData
): Promise<void> {
  // 1. 生成暫定 ID 與暫定 entry
  const tempId = `__temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const templates = get().templates ?? [];
  const tpl = templates.find(t => t.id === data.templateId);

  const optimisticEntry: ScheduleEntryLite = {
    id: tempId,
    userId: data.userId,
    date: data.date,
    startTime: data.startTime,
    endTime: data.endTime,
    breakStart: data.breakStart,
    breakEnd: data.breakEnd,
    template: tpl ? { id: tpl.id, name: tpl.name, color: tpl.color, kind: tpl.kind } : null,
    isPublished: false,
    isOptimistic: true,
  };

  // 2. 立即將暫定 entry 加入 state（畫面立刻有顏色）
  set((state) => ({ schedules: [...state.schedules, optimisticEntry] }));

  try {
    // 3. 發送 POST
    const res = await api.post<{ schedule: ScheduleEntryLite }>('/schedules', data);
    // 4. 成功：用真實 entry 替換暫定 entry
    set((state) => ({
      schedules: state.schedules.map(s => s.id === tempId ? res.schedule : s),
    }));
  } catch (err) {
    // 5. 失敗：移除暫定 entry 並 rethrow
    set((state) => ({
      schedules: state.schedules.filter(s => s.id !== tempId),
    }));
    throw err;
  }
}

/**
 * 樂觀刪除：先從 state 移除，POST 失敗再恢復
 */
export async function optimisticDelete(
  set: (fn: (state: { schedules: ScheduleEntryLite[] }) => Partial<{ schedules: ScheduleEntryLite[] }>) => void,
  get: () => { schedules: ScheduleEntryLite[] },
  id: string
): Promise<void> {
  // 備份被刪除的 entry（供失敗時回復）
  const removed = get().schedules.find(s => s.id === id);

  set((state) => ({
    schedules: state.schedules.filter(s => s.id !== id),
  }));

  try {
    await api.delete(`/schedules/${id}`);
  } catch (err) {
    // 回復
    if (removed) {
      set((state) => ({ schedules: [...state.schedules, removed] }));
    }
    throw err;
  }
}
